export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Electrical Fundamentals, Circuit Theory & Electrical Safety',
    subtitle: 'The Foundation Every Electrical Student Needs',
    intro: [
      'Every new group starts with the same challenge: voltage, current, resistance, Ohm’s law, series and parallel circuits, meters and safety rules, all before the first real circuit.',
      'The theory looks simple on paper, but students only get it right when they can see the circuit, the meter reading and the hazard at the same time.',
      'This volume turns electrical fundamentals into labeled diagrams, worked examples, step-by-step sequences, ❌/✅ comparisons, teaching cues and lab drills you can use from the very first class.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Voltage, current & resistance',
      'The basic electric circuit',
      'DC vs. AC',
      'Ohm’s law with worked examples',
      'Electrical power, energy & the power formula wheel',
      'Series, parallel & series-parallel circuits',
      'Kirchhoff’s laws',
      'Resistors, capacitors & the color code',
      'Magnetism & electromagnetic induction',
      'AC waveform terms & power factor basics',
      'Wires, cables & conductor color codes (NEC vs. IEC)',
      'The digital multimeter: voltage, current & continuity',
      'Meter safety categories (CAT II–IV)',
      'Electrical PPE head to toe & insulated tools',
      'Lockout/tagout & test before touch (live-dead-live)',
      'Safety orientation day & 4-week fundamentals plan',
    ],
    result: 'Give every student a safe, confident start with circuits, meters and electrical safety before they wire anything.',
  },
  {
    number: 'Volume 02',
    title: 'Residential Electrical Wiring, Installations & Circuit Protection',
    subtitle: 'Wire It Right, Protect It, Test It',
    intro: [
      'Residential wiring is where most students install their first real circuits, and where panels, receptacles and switching cause the most confusion.',
      'Without a clear method, small mistakes turn into reversed polarity, loose terminations, overfilled boxes or a GFCI wired backwards.',
      'This volume organizes residential work into panel diagrams, device wiring visuals, switching layouts, protection comparisons, testing sequences and training-board exercises.',
    ],
    popular: true,
    popularLabel: 'CORE SKILLS',
    bulletsIntro: 'Inside:',
    bullets: [
      'How power reaches a home & supply systems around the world',
      'Service entrance, meter & main panel / consumer unit',
      'Grounding electrode system & grounding vs. bonding',
      'Circuit breakers & fuses',
      'GFCI / RCD, AFCI & surge protection',
      'Residential wiring methods, boxes & box fill',
      'Receptacle anatomy & wiring a duplex receptacle',
      'GFCI receptacle: LINE vs. LOAD',
      'Single-pole switch circuits',
      'Three-way & four-way switching (US) / two-way & intermediate (UK)',
      'Lighting circuits, ceiling fans & luminaires',
      'Kitchen, bathroom, laundry & outdoor circuits',
      'Branch circuit planning & load calculation basics',
      'Rough-in & trim-out (first & second fix)',
      'Polarity testing & inspection before energizing',
      'Common wiring mistakes & 4-week residential plan',
    ],
    result: 'Help students plan, install, protect and test safe residential circuits from the panel to the last device.',
  },
  {
    number: 'Volume 03',
    title: 'Commercial & Industrial Electrical Systems, Motors & Controls',
    subtitle: 'Three-Phase Power, Motors and Control Circuits',
    intro: [
      'Commercial and industrial work adds three-phase power, transformers, motors and control circuits, all at the same time.',
      'Wye and delta systems, motor connections and seal-in circuits are hard to explain until students can see the power circuit and the control circuit side by side.',
      'This volume covers commercial and industrial systems with distribution diagrams, motor connection drawings, ladder diagrams, ❌/✅ cards, calculation examples and motor-control lab exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Three-phase power basics',
      'Wye (star) & delta systems',
      'Three-phase power calculations',
      'Transformer principles, connections & kVA sizing',
      'Switchboards, panelboards & motor control centers',
      'Commercial wiring methods & conduit bending basics',
      'Three-phase induction motor construction',
      'Reading a motor nameplate',
      'Motor speed & slip',
      'Dual-voltage connections & reversing a motor',
      'Motor starting methods & motor protection',
      'Contactors, control relays & timing relays',
      'Ladder diagram basics',
      'Three-wire start/stop control with seal-in',
      'Forward/reverse control with interlocks',
      'Wye-delta starter control & 4-week motors plan',
    ],
    result: 'Help students understand three-phase systems and wire, start, reverse and protect motors with confidence.',
  },
  {
    number: 'Volume 04',
    title: 'Electrical Diagrams, Testing, Troubleshooting & Fault Diagnosis',
    subtitle: 'Read the Diagram, Test Safely, Find the Fault',
    intro: [
      'An electrician who cannot read a diagram or test safely cannot find a fault, and troubleshooting is where many students feel lost.',
      'Schematic symbols, ladder cross-references, meter technique and a systematic fault-finding method are hard to teach with words alone.',
      'This volume explains electrical diagrams, testing and fault diagnosis with annotated schematics, test sequences, flow charts, case studies and fault-insertion exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Types of electrical drawings',
      'Schematic symbols: sources, loads, protection, contacts & coils',
      'Reading a schematic & wiring diagram vs. schematic',
      'One-line diagrams & ladder cross-referencing',
      'Wire numbers, terminal strips & panel schedules',
      'Multimeter technique: voltage & current testing',
      'Resistance & continuity testing',
      'Testing fuses, breakers, switches & coils',
      'Insulation resistance testing',
      'Testing three-phase motors & capacitors',
      'Testing GFCI / RCD devices',
      'Voltage drop testing under load',
      'The systematic troubleshooting method & half-split technique',
      'Open circuits, short circuits, ground faults & high-resistance connections',
      'Troubleshooting lighting, receptacle & motor control circuits',
      'Case studies, fault flow charts & safe testing practices',
    ],
    result: 'Help students read any diagram, test safely and track down faults with a clear, repeatable method.',
  },
  {
    number: 'Volume 05',
    title: 'Industrial Automation, PLCs, Variable Frequency Drives & Smart Electrical Systems',
    subtitle: 'From Relay Logic to PLCs, Drives and Smart Systems',
    intro: [
      'Automation is where electrical training meets programming: PLC hardware, I/O wiring, ladder logic, sensors and drives.',
      'Sinking vs. sourcing, the scan cycle, timers, analog signals and VFD parameters are hard to teach until students can see the wiring and the logic together.',
      'This volume explains industrial automation, PLCs, VFDs and smart electrical systems with I/O wiring diagrams, ladder examples, parameter tables, troubleshooting charts and automation lab exercises.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Relay logic vs. PLC control',
      'PLC hardware & the scan cycle',
      'Digital inputs & outputs, sinking vs. sourcing',
      'Analog inputs & outputs (4–20 mA, 0–10 V)',
      'Ladder logic basics: AND, OR, NOT & seal-in',
      'Latch/unlatch, timers & counters',
      'IEC 61131-3 languages, FBD & structured text',
      'Proximity, photoelectric & process sensors',
      'Encoders, actuators & solenoid valves',
      'HMI basics & industrial communication',
      'Variable frequency drives: inside a VFD & V/Hz',
      'VFD power wiring, parameter setup & control methods',
      'VFD fault codes & troubleshooting',
      'Safety circuits & emergency stops',
      'Smart electrical systems, building automation & energy monitoring',
      'Troubleshooting PLC systems & 4-week automation plan',
    ],
    result: 'Help students wire, program and troubleshoot PLCs, VFDs and smart electrical systems step by step.',
  },
  {
    number: 'Volume 06',
    title: 'Electrical Instruction, Workshop Training & Practical Assessments',
    subtitle: 'Teach With a System',
    intro: [
      'Knowing how to do electrical work is not the same as knowing how to teach it.',
      'Electrical instructors need clear methods for lessons, demonstrations, lab safety, energizing student circuits, mixed-level groups, practical exercises and assessment.',
      'This volume gives you lesson plans, demonstration methods, lab layouts, safety sign-offs, circuit-building activities, competency checklists, rubrics and a full course calendar ready to use.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'The electrical instructor’s role',
      'Planning a training program & writing learning objectives',
      'Electrical lesson plan template',
      'The 4-step demonstration method',
      'Electrical lab layout, stations & safety rules poster',
      'Student safety orientation & sign-off',
      'Energizing a student circuit safely',
      'Ready lesson plans: Ohm’s law, multimeter, LOTO & switching',
      'Ready lesson plans: motor control, first PLC program & VFD setup',
      'Circuit-building activities & workshop exercises',
      'Coaching at the bench & correcting common mistakes',
      'Differentiated instruction & questioning techniques',
      'Formative assessment & practical assessment design',
      'Competency checklists & practical assessment rubric',
      'Written test blueprint & tracking student progress',
      '16-week electrical course calendar',
    ],
    result: 'Plan, deliver and assess every lesson, lab and course with structure, from first-time beginners to advanced students.',
  },
]
